import { Box, Skeleton, useTheme } from '@mui/material';

import { LAYOUT_MAX_WIDTH } from '@/constants';

interface SportsSidebarSkeletonProps {
  isMobile?: boolean;
}

// "Live" + up to 3 dynamic categories + "More" — matches SportsMobileSidebar's
// item count (allItems.slice(0, 3) plus its two fixed buttons).
const MOBILE_ITEM_COUNT = 5;

export const SportsSidebarSkeleton = ({ isMobile = false }: SportsSidebarSkeletonProps) => {
  const theme = useTheme();

  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          position: 'sticky',
          top: { xs: '56px', sm: '60px', md: '64px' },
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: 'background.default',
          width: '100%',
          mx: { xs: -1, sm: -1.5, md: -2, lg: -2.5 },
          px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            maxWidth: LAYOUT_MAX_WIDTH,
            mx: 'auto',
            pb: 1,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {Array.from({ length: MOBILE_ITEM_COUNT }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                flexShrink: 0,
                minWidth: 64,
                height: 64,
                borderRadius: 1,
              }}
            >
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={36} height={14} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        minHeight: 400,
        boxSizing: 'border-box',
      }}
    >
      {/* Live / Futures buttons placeholders */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Skeleton variant="rounded" width="50%" height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" width="50%" height={40} sx={{ borderRadius: 1 }} />
      </Box>

      <Box sx={{ height: 1, bgcolor: theme.palette.divider, my: 1, opacity: 0.1 }} />

      {/* All Sports section title */}
      <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1.5 }} />

      {/* List items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1 }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ opacity: 0.6 }} />
            <Skeleton variant="text" width="60%" height={20} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="text" width={20} height={20} sx={{ opacity: 0.4 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
